import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { gtmClick, gtmPhoneClick } from "@/lib/gtm";

const links = [
  { href: "/packages", label: "Packages" },
  { href: "/services", label: "Services" },
  { href: "/portfolio", label: "Portfolio" },
  { href: "/how-it-works", label: "How It Works" },
  { href: "/blog", label: "Blog" },
  { href: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled ? "bg-white border-b-2 border-black shadow-sm" : "bg-white/95 border-b-2 border-black"
      }`}
    >
      <div className="container-main flex items-center justify-between h-16 md:h-18">
        {/* Logo */}
        <Link to="/" className="flex items-center group" data-testid="navbar-logo">
          <img
            src="/logo.png"
            alt="No Fuss Websites"
            className="h-12 w-auto group-hover:-translate-y-0.5 transition-transform"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-150 ${
                location.pathname === l.href
                  ? "bg-black text-white"
                  : "hover:bg-[#FFD600] hover:text-black"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a href="tel:0466476264" className="text-sm font-bold hover:text-[#0055FF] transition-colors" data-testid="navbar-phone" onClick={() => gtmPhoneClick("navbar_desktop")}>
            0466 476 264
          </a>
          <Link
            to="/purchase"
            className="btn-primary text-sm px-6 py-2.5"
            data-testid="navbar-cta"
            onClick={() => gtmClick("navbar_buy_now")}
          >
            Buy Now
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 border-2 border-black rounded-lg hover:bg-[#FFD600] transition-colors"
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="md:hidden border-t-2 border-black bg-white" data-testid="navbar-mobile-menu">
          <div className="container-main py-4 flex flex-col gap-2">
            {links.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="px-4 py-3 font-semibold border-2 border-black rounded-xl hover:bg-[#FFD600] transition-colors"
              >
                {l.label}
              </Link>
            ))}
            <div className="border-t-2 border-black mt-2 pt-4 flex flex-col gap-3">
              <a href="tel:0466476264" className="text-center font-bold py-3 border-2 border-black rounded-xl hover:bg-[#FFD600] transition-colors" onClick={() => gtmPhoneClick("navbar_mobile")}>
                Call: 0466 476 264
              </a>
              <Link to="/contact" className="btn-primary text-center" onClick={() => gtmClick("navbar_free_quote")}>
                Get a Free Quote
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
